import { readonly, ref, watch } from 'vue';
import { createAuth0 } from '@auth0/auth0-vue';

let auth0Client = null;

const accessTokenState = ref('');

export const accessTokenRef = readonly(accessTokenState);

// Capture Auth0 redirect-callback errors from the URL once at module load, before
// the SDK's __checkSession strips ?error=&state= via history.replaceState.
// Do not use the shared auth.error ref as a circuit breaker — it is set by any
// failed SDK call (including getAccessTokenSilently), not only terminal login errors.
const captureAuthCallbackError = () => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('error') && params.get('state')) {
        return {
            error: params.get('error'),
            error_description: params.get('error_description') || '',
        };
    }

    return null;
};

export const authCallbackError = captureAuthCallbackError();

function readConfig() {
    const config = {
        domain: window.__KEAP__.VUE_APP_AUTH0_DOMAIN,
        clientId: window.__KEAP__.VUE_APP_AUTH0_CLIENT_ID,
        audience: window.__KEAP__.VUE_APP_AUTH0_AUDIENCE,
    };

    const missing = Object.entries(config)
        .filter(([, value]) => !value)
        .map(([key]) => key);

    if (missing.length) {
        throw new Error(
            `Auth0 SPA application is not provisioned for this environment: missing ${missing.join(', ')}. `
            + 'Update packages/share-app/config/{env}.json with the Auth0 domain, client_id, and audience.',
        );
    }

    return config;
}

export function createAuth0Plugin({ onRedirectCallback } = {}) {
    if (auth0Client) return auth0Client;

    const { domain, clientId, audience } = readConfig();

    auth0Client = createAuth0({
        domain,
        clientId,
        authorizationParams: {
            redirect_uri: window.location.origin,
            audience,
        },
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
        errorPath: '/auth-error',
        onRedirectCallback,
    });

    return auth0Client;
}

export function getAuth0Plugin() {
    if (!auth0Client) {
        throw new Error('Auth0 client has not been initialized. Call createAuth0Plugin() first.');
    }

    return auth0Client;
}

export async function getAccessToken({ ignoreCache = false } = {}) {
    const client = getAuth0Plugin();
    const token = await client.getAccessTokenSilently(
        ignoreCache ? { cacheMode: 'off' } : undefined,
    );

    if (token) accessTokenState.value = token;

    return token;
}

export function auth0Logout(logoutOptions) {
    accessTokenState.value = '';

    return getAuth0Plugin().logout(logoutOptions);
}

export function startTokenSync({ onError } = {}) {
    const client = getAuth0Plugin();

    const refresh = async () => {
        if (!client.isAuthenticated.value) {
            accessTokenState.value = '';

            return;
        }

        try {
            await getAccessToken();
        } catch (err) {
            accessTokenState.value = '';
            if (onError) onError(err);
        }
    };

    watch(() => client.isAuthenticated.value, refresh);
}
